import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import type { ListOverrideAudience } from './list-override.entity';
import { RbacRoleEntity } from './rbac-role.entity';

/**
 * An audience-scoped tab strip (PD-14278): which tabs an entity's page shows
 * and in what order, for everyone in the tenant or for one role. Replaces
 * the named tab configurations (`list_tab_configuration`), which each held a
 * whole photograph of the strip. This is a delta: `tabOrder` is the order as
 * last authored (null when never reordered; may include `saved:<id>` entries
 * that pin a saved list into the strip) and `hiddenIds` the tabs taken out.
 * A tab shipped later still reaches every audience, placed beside its
 * standard neighbor.
 *
 * Resolution: standard order → everyone → role (highest on the role ladder
 * among the viewer's roles) → the person's own strip (`list_user_tabs`).
 */
@Index('idx_list_tab_strip_slot', ['listKey', 'audience', 'fkRbacRoleId'], {
	unique: true,
})
@Index('idx_list_tab_strip_list_key', ['listKey'])
@Entity('list_tab_strip')
export class ListTabStripEntity extends BaseEntity {
	@Column('varchar', { name: 'listKey', length: 100 })
	public listKey!: string;

	@Column('enum', {
		name: 'audience',
		enum: ['organization', 'role'],
		default: 'organization',
	})
	public audience!: ListOverrideAudience;

	@Column('int', { name: 'fk_rbac_role__id', unsigned: true, nullable: true })
	public fkRbacRoleId!: number | null;

	@ManyToOne(() => RbacRoleEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
		nullable: true,
	})
	@JoinColumn([{ name: 'fk_rbac_role__id', referencedColumnName: 'id' }])
	public fkRbacRole!: RbacRoleEntity | null;

	@Column('json', { name: 'tabOrder', nullable: true })
	public tabOrder!: Array<string> | null;

	@Column('json', { name: 'hiddenIds' })
	public hiddenIds!: Array<string>;
}
